import React, { useMemo, useState } from "react";
import { api, boutiqueOf, roleLabel } from "@/lib/api";
import { X, MapPin, Users2, Check, Trash2, Pencil, Info } from "lucide-react";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";

function fmtRange(startISO, endISO, allDay) {
  try {
    const s = new Date(startISO);
    const e = new Date(endISO);
    const dateLabel = s.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
    if (allDay) return `${dateLabel} · All day`;
    const t = (d) => d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    return `${dateLabel} · ${t(s)} – ${t(e)}`;
  } catch { return startISO; }
}

const RSVP_STATES = [
  { key: "yes", label: "Attending" },
  { key: "maybe", label: "Maybe" },
  { key: "no", label: "Can't make it" },
];

export default function EventDetailDialog({ event, directory, onClose, onEdit, onDeleted, onRsvpChanged }) {
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const requiredUsers = useMemo(() => {
    const map = new Map((directory.users || []).map((u) => [u.user_id, u]));
    return (event.required_attendees || []).map((uid) => map.get(uid) || { user_id: uid, name: uid });
  }, [event.required_attendees, directory.users]);

  const audienceSummary = useMemo(() => {
    const v = event.visibility || {};
    const bits = [];
    if (v.everyone) bits.push("Everyone at Rainbow");
    if ((v.boutiques || []).length) bits.push(`Boutiques: ${v.boutiques.join(", ")}`);
    if ((v.roles || []).length) bits.push(`Roles: ${v.roles.map((r) => roleLabel[r] || r).join(", ")}`);
    if ((v.user_ids || []).length) {
      const map = new Map((directory.users || []).map((u) => [u.user_id, u.name]));
      bits.push(`Individuals: ${v.user_ids.map((uid) => map.get(uid) || uid).slice(0, 5).join(", ")}${v.user_ids.length > 5 ? ` +${v.user_ids.length - 5}` : ""}`);
    }
    if (bits.length === 0) return "Just the creator and required attendees";
    return bits.join(" · ");
  }, [event.visibility, directory.users]);

  const sendRsvp = async (status) => {
    setSaving(true);
    try {
      const res = await api.post(`/calendar/events/${event.id}/rsvp`, { status });
      toast.success("RSVP saved.");
      onRsvpChanged?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save RSVP.");
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/calendar/events/${event.id}`);
      toast.success("Event deleted.");
      onDeleted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete event.");
    } finally { setSaving(false); }
  };

  const summary = event.rsvp_summary || { yes: 0, no: 0, maybe: 0, no_response: 0, required_total: (event.required_attendees || []).length };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 mobile-modal-wrap"
      onClick={onClose}
      data-testid="event-detail-dialog"
    >
      <div
        className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto card-shadow mobile-modal-card"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 bg-white border-b border-stone-100 px-6 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="label-eyebrow">Event</div>
            <h2 className="font-serif-display text-xl text-stone-900 truncate" data-testid="event-detail-title">{event.title}</h2>
            <div className="text-xs text-stone-500 mt-1">{fmtRange(event.start_at, event.end_at, event.all_day)}</div>
          </div>
          <button onClick={onClose} data-testid="event-detail-close" className="p-2 rounded-full text-stone-400 hover:bg-stone-100 hover:text-stone-900 shrink-0">
            <X size={18} strokeWidth={1.5} />
          </button>
        </header>

        <div className="px-6 py-6 space-y-6">
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-[13px] text-stone-600">
            {event.location && (
              <span className="inline-flex items-center gap-1.5"><MapPin size={12} className="text-stone-400" /> {event.location}</span>
            )}
            {event.boutique_code && (
              <span className="inline-flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${boutiqueOf(event.boutique_code).dot}`} />
                Hosted by {boutiqueOf(event.boutique_code).short}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5"><Users2 size={12} className="text-stone-400" /> {summary.required_total} required</span>
          </div>

          {event.description && (
            <div className="rounded-2xl bg-stone-50 border border-stone-100 p-4 text-sm text-stone-700 leading-relaxed whitespace-pre-wrap" data-testid="event-detail-description">
              {event.description}
            </div>
          )}

          {/* RSVP */}
          {event.is_required && (
            <section data-testid="event-detail-rsvp">
              <div className="label-eyebrow mb-2">Your RSVP</div>
              <div className="flex flex-wrap gap-2">
                {RSVP_STATES.map((opt) => {
                  const on = event.my_rsvp === opt.key;
                  const cls =
                    opt.key === "yes"   ? (on ? "bg-emerald-600 text-white border-emerald-600" : "border-emerald-200 text-emerald-800 hover:bg-emerald-50")
                  : opt.key === "maybe" ? (on ? "bg-amber-600 text-white border-amber-600" : "border-amber-200 text-amber-800 hover:bg-amber-50")
                                        : (on ? "bg-rose-600 text-white border-rose-600" : "border-rose-200 text-rose-800 hover:bg-rose-50");
                  return (
                    <button
                      key={opt.key}
                      disabled={saving}
                      onClick={() => sendRsvp(opt.key)}
                      data-testid={`event-rsvp-${opt.key}`}
                      className={`px-4 py-1.5 rounded-full text-xs border font-medium transition-colors disabled:opacity-50 ${cls}`}
                    >
                      {on && <Check size={11} className="inline mr-1 -mt-0.5" strokeWidth={3} />}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-stone-500 mt-2">
                {event.my_rsvp ? `You said: ${RSVP_STATES.find((r) => r.key === event.my_rsvp)?.label}` : "Awaiting your response."}
              </div>
            </section>
          )}

          {/* Required attendees list */}
          {requiredUsers.length > 0 && (
            <section data-testid="event-detail-attendees">
              <div className="label-eyebrow mb-2">Required attendees · {summary.yes} yes · {summary.maybe} maybe · {summary.no} no · {summary.no_response} awaiting</div>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {requiredUsers.map((u) => {
                  const r = (event.rsvps || {})[u.user_id];
                  const status = r?.status;
                  const dot =
                    status === "yes"   ? "bg-emerald-500" :
                    status === "maybe" ? "bg-amber-500" :
                    status === "no"    ? "bg-rose-500" : "bg-stone-300";
                  return (
                    <div key={u.user_id} className="flex items-center gap-2 text-xs px-2 py-1.5 rounded-xl border border-stone-100 bg-white" data-testid={`event-detail-attendee-${u.user_id}`}>
                      <Avatar name={u.name} boutique={u.boutique_code} picture={u.picture} size={22} />
                      <div className="min-w-0 flex-1">
                        <div className="text-stone-900 truncate">{u.name}</div>
                        <div className="text-[10px] text-stone-500 truncate">{(u.boutique_code || "RNB")} · {roleLabel[u.role] || u.role || ""}</div>
                      </div>
                      <span className={`w-2 h-2 rounded-full ${dot}`} title={status || "no response"} />
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section>
            <div className="label-eyebrow mb-2 flex items-center gap-1.5"><Info size={11} /> Audience</div>
            <div className="text-[13px] text-stone-600 leading-relaxed" data-testid="event-detail-audience">{audienceSummary}</div>
            <div className="text-[11px] text-stone-400 mt-2">Created by {event.created_by_name}</div>
          </section>
        </div>

        {event.can_edit && (
          <footer className="sticky bottom-0 bg-white border-t border-stone-100 px-6 py-4 flex items-center justify-between gap-2">
            {confirmDelete ? (
              <div className="flex items-center gap-2 text-xs text-rose-700">
                <span>Delete this event?</span>
                <button onClick={doDelete} disabled={saving} data-testid="event-delete-confirm" className="px-3 py-1 rounded-full bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50">Yes, delete</button>
                <button onClick={() => setConfirmDelete(false)} className="px-3 py-1 rounded-full border border-stone-200 hover:bg-stone-100 text-stone-600">Cancel</button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(true)}
                data-testid="event-delete"
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs text-rose-700 hover:bg-rose-50 border border-rose-100"
              >
                <Trash2 size={12} /> Delete
              </button>
            )}
            <button
              onClick={onEdit}
              data-testid="event-edit"
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm bg-stone-900 text-white hover:bg-stone-800"
            >
              <Pencil size={12} /> Edit event
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}
